import { useState } from "react";
import { ApiError, apiFetch } from "@/lib/apiClient";
import { env } from "@/lib/env";
import type { RequiresConsent, UserProfile } from "@/types/session";

/**
 * Conditional, post-identity Terms & Privacy consent screen (PDR 011 D8 / ADR 031,
 * #4204). Rendered by the App gate chain ONLY when the session signals
 * `requiresConsent.required` — i.e. the user has no acceptance on record for the
 * current required version (or holds a stale one). It is the seam the two-screen
 * identity-first SSO model provides: identity sign-in → (this screen) → the
 * mail+calendar data-permission grant.
 *
 * This is the versioned flow, live only while `TC_VERSIONED_CONSENT` is ON (the
 * backend reports `required:false` while OFF, so this never renders then — the
 * implicit footer #4224 governs instead). On agree it records a durable, versioned
 * acceptance via POST /auth/accept-terms (the version id is resolved server-side)
 * and refreshes the session, after which the gate falls through.
 *
 * Provider-agnostic: SSO (Google/Microsoft) and magic-link identities render the
 * same screen; only the recorded `source` differs.
 */
export interface VersionedTermsGateProps {
  user: UserProfile;
  authToken: string | null;
  requiresConsent: RequiresConsent;
  /** Called after a successful acceptance — typically the session `refresh`. */
  onAccepted: () => void;
}

/** Which flow captured the consent (ADR 031 §2 / the accept-terms contract). */
function resolveSource(
  user: UserProfile,
): "sso" | "magic_link_connect" | "re_consent" {
  // A user who already holds an acceptance is being re-prompted for a stale
  // version — that's a re-consent regardless of how they signed in.
  if (user.agreementsAcceptedAt) return "re_consent";
  // First-time consent: SSO identities carry a loginProvider; magic-link does not.
  return user.loginProvider ? "sso" : "magic_link_connect";
}

export function VersionedTermsGate({
  user,
  authToken,
  requiresConsent,
  onAccepted,
}: VersionedTermsGateProps) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefer the registry version's own content URL; fall back to the centralized
  // public Terms URL (#4224) when the version carries none.
  const termsUrl = requiresConsent.version?.contentUrl || env.termsUrl;

  const handleAgree = async () => {
    if (!agreed) {
      setError(
        "Please agree to the Terms and Privacy Policy to continue.",
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiFetch<{
        success: boolean;
        versionId?: number;
        acceptedAt?: number;
      }>("auth/accept-terms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source: resolveSource(user) }),
        authToken: authToken ?? undefined,
      });
      if (response?.success) {
        // Refresh the session so requiresConsent.required flips to false and the
        // gate falls through to the next step (plan / connect / onboarding).
        onAccepted();
        return;
      }
      setError("Failed to record acceptance. Please try again.");
      setSubmitting(false);
    } catch (thrown) {
      if (thrown instanceof ApiError) {
        const payload = thrown.payload as { error?: string } | undefined;
        setError(payload?.error ?? "Request failed. Please try again.");
      } else {
        setError("Request failed. Please try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[#f8f7f5] p-6 xv-scroll">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-sm">
        <h1 className="mb-2 text-center text-xl font-semibold text-gray-900">
          Before you continue
        </h1>
        <p className="mb-6 text-center text-sm text-gray-600">
          Please review and accept our Terms and Privacy Policy to keep using
          Xavior.
        </p>

        <label className="mb-4 flex items-start justify-center gap-3 text-left">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
          />
          <span className="text-sm text-gray-600">
            I agree to the{" "}
            <a
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-600 hover:underline"
            >
              Terms and Privacy Policy
            </a>
          </span>
        </label>

        {error && (
          <p className="mb-3 text-center text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={handleAgree}
          disabled={submitting || !agreed}
          className="w-full rounded-xl bg-sky-600 py-3 text-sm font-medium text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? "Recording…" : "Agree & Continue"}
        </button>
      </div>
    </div>
  );
}
